import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import Header from '../components/Header';
import Alert from '../components/common/Alert';
import { dashboardClient } from '../utils/optimizedApi';
import './CohortRetention.css';
import './RepeatAnalysis.css';

const LtvAnalysis = () => {
  // Same window as the GMV month sheet: Aug 2021 through today
  const today = new Date();
  const defaultStartDate = '2021-08-01';
  const defaultEndDate = format(today, 'yyyy-MM-dd');
  
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [startDateObj, setStartDateObj] = useState(new Date(defaultStartDate));
  const [endDateObj, setEndDateObj] = useState(new Date(defaultEndDate));
  const [cohortData, setCohortData] = useState(null);
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [serverTotals, setServerTotals] = useState({ users: 0, total: 0, periodTotals: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [hasSearched, setHasSearched] = useState(false); // Track if user has performed a search
  const [displayMode, setDisplayMode] = useState('absolute'); // GMV files were rupee amounts

  // Location/substore filter state (similar to Dashboard)
  const [, setSubstores] = useState([]);
  const [cityHubs, setCityHubs] = useState({});
  const [hubToSubstoreMapping, setHubToSubstoreMapping] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState({
    inScope: {
      delhi: [],
      bangalore: [],
      blank: false
    },
    outScope: []
  });
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    inScope: false,
    outScope: false
  });
  const [expandedCities, setExpandedCities] = useState({
    delhi: false,
    bangalore: false
  });
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Fetch substores/hubs data for location filter
  const fetchSubstores = useCallback(async () => {
    try {
      setLoadingLocations(true);
      const response = await dashboardClient.get('/substores');
      if (response.data.success) {
        setSubstores(response.data.substores || []);
        setCityHubs(response.data.cityHubs || {});
        setHubToSubstoreMapping(response.data.hubToSubstoreMapping || []);
      }
    } catch (error) {
      console.error('Error fetching substores:', error);
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  // Load location data on mount
  useEffect(() => {
    fetchSubstores();
  }, [fetchSubstores]);


  // Helper function to convert hubs to substores
  const convertHubsToSubstores = useCallback((hubs) => {
    if (!hubToSubstoreMapping || !Array.isArray(hubs)) return [];
    return hubs.flatMap(hub => {
      const mapping = hubToSubstoreMapping.find(m => m.hub === hub);
      return mapping ? mapping.substores : [];
    });
  }, [hubToSubstoreMapping]);

  // Get selected substores from location selection
  const getSelectedSubstores = useCallback(() => {
    const delhiSubstores = convertHubsToSubstores(selectedLocations.inScope.delhi);
    const bangaloreSubstores = convertHubsToSubstores(selectedLocations.inScope.bangalore);
    const outScopeSubstores = convertHubsToSubstores(selectedLocations.outScope);
    
    const allSelectedSubstores = [
      ...delhiSubstores,
      ...bangaloreSubstores,
      ...outScopeSubstores
    ];
    
    // Sentinel so axios/query string keep Blank (empty string is dropped by parsers)
    if (selectedLocations.inScope.blank) {
      allSelectedSubstores.push('__blank__');
    }
    
    return allSelectedSubstores;
  }, [selectedLocations, convertHubsToSubstores]);

  // Update date objects when string dates change
  useEffect(() => {
    setStartDateObj(new Date(startDate));
    setEndDateObj(new Date(endDate));
  }, [startDate, endDate]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationDropdown && !event.target.closest('.location-multiselect')) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLocationDropdown]);

  // Handle start date change
  const handleStartDateChange = (date) => {
    if (date) {
      setStartDateObj(date);
      setStartDate(format(date, 'yyyy-MM-dd'));
    }
  };

  // Handle end date change
  const handleEndDateChange = (date) => {
    if (date) {
      setEndDateObj(date);
      setEndDate(format(date, 'yyyy-MM-dd'));
    }
  };

  const fetchCohortData = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    // Validate date range
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start >= end) {
      setError('Start date must be before end date');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      // Get selected substores
      const selectedSubstores = getSelectedSubstores();
      
      // Send dates in YYYY-MM-DD format so backend can interpret them as IST dates
      // The backend will convert IST dates to UTC for database queries
      const params = {
          startDate: startDate,
          endDate: endDate
      };
      
      if (selectedSubstores.length > 0) {
        params.substores = selectedSubstores.join(',');
      }
      
      const response = await axios.get('/ltv-analysis', {
        params,
        timeout: 600000,
      });

      if (response.data.success) {
        setCohortData(response.data.data);
        setColumnHeaders(response.data.columns || []);
        setServerTotals(response.data.totals || { users: 0, total: 0, periodTotals: [] });
        setCacheInfo({
          fromCache: response.data.fromCache,
          cacheType: response.data.fromCache ? 'exact' : 'fresh',
          generatedAt: response.data.generatedAt,
          processingTimeMs: response.data.processingTimeMs,
          substoreFiltered: response.data.substoreFiltered || false,
          substores: response.data.substores || []
        });
      } else {
        setError('Failed to fetch spend analysis');
      }
    } catch (err) {
      console.error('Error fetching spend analysis:', err);
      const errorMessage = err.response?.data?.details || err.response?.data?.error || err.message || 'An error occurred while fetching spend analysis';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Manual search handler
  const handleSearch = () => {
    fetchCohortData();
  };

  const handleDisplayModeChange = (newMode) => {
    setDisplayMode(newMode);
  };

  const formatMoney = (value) => {
    return Number(value || 0).toLocaleString('en-IN', {
      maximumFractionDigits: 2
    });
  };

  // Location handling functions (similar to Dashboard)
  const handleLocationToggle = (category, city, hub) => {
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      
      if (category === 'inScope') {
        if (city === 'blank') {
          newLocations.inScope.blank = !newLocations.inScope.blank;
        } else {
          const cityKey = city.toLowerCase();
          if (newLocations.inScope[cityKey].includes(hub)) {
            newLocations.inScope[cityKey] = newLocations.inScope[cityKey].filter(h => h !== hub);
          } else {
            newLocations.inScope[cityKey] = [...newLocations.inScope[cityKey], hub];
          }
        }
      } else if (category === 'outScope') {
        if (newLocations.outScope.includes(hub)) {
          newLocations.outScope = newLocations.outScope.filter(h => h !== hub);
        } else {
          newLocations.outScope = [...newLocations.outScope, hub];
        }
      }
      
      return newLocations;
    });
  };

  const handleSelectAllInScope = (city) => {
    const cityKey = city === 'Bengaluru' ? 'bangalore' : city.toLowerCase();
    const cityHubsList = cityHubs[city] || [];
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      newLocations.inScope[cityKey] = cityHubsList;
      return newLocations;
    });
  };

  const handleClearAllInScope = (city) => {
    const cityKey = city === 'Bengaluru' ? 'bangalore' : city.toLowerCase();
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      newLocations.inScope[cityKey] = [];
      return newLocations;
    });
  };

  const handleSelectAllInScopeItems = () => {
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      newLocations.inScope.delhi = cityHubs.Delhi || [];
      newLocations.inScope.bangalore = cityHubs.Bengaluru || [];
      newLocations.inScope.blank = true;
      return newLocations;
    });
  };

  const handleClearAllInScopeItems = () => {
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      newLocations.inScope.delhi = [];
      newLocations.inScope.bangalore = [];
      newLocations.inScope.blank = false;
      return newLocations;
    });
  };

  const handleSelectAllOutScope = () => {
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      const allInScopeHubs = [
        ...(cityHubs.Delhi || []),
        ...(cityHubs.Bengaluru || [])
      ];
      const allHubs = [
        ...(cityHubs.Delhi || []),
        ...(cityHubs.Bengaluru || []),
        ...(cityHubs['Pan India'] || [])
      ];
      newLocations.outScope = allHubs.filter(hub => 
        !allInScopeHubs.includes(hub)
      );
      return newLocations;
    });
  };

  const handleClearAllOutScope = () => {
    setSelectedLocations(prev => {
      const newLocations = { ...prev };
      newLocations.outScope = [];
      return newLocations;
    });
  };

  const toggleCategoryExpansion = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleCityExpansion = (city) => {
    setExpandedCities(prev => ({
      ...prev,
      [city]: !prev[city]
    }));
  };

  // Handle keyboard shortcuts
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !loading && startDate && endDate) {
      handleSearch();
    }
  };

  // Removed useEffect for fetching earliest date - no API calls on page load
  // Users will manually set their preferred date ranges

  const getColumnHeaders = () => {
    if (columnHeaders.length > 0) return columnHeaders;
    return ['Under 250', '250-500', '500-1000', '1000-2000', '2000-5000', '5000-10000', 'Above 10000'];
  };

  const formatPeople = (value) => Number(value || 0).toLocaleString('en-IN');

  const formatHeaderTotal = (value) => formatPeople(value);

  const formatCellValue = (cell) => {
    if (!cell) return displayMode === 'percentage' ? '0.0%' : '0';
    if (displayMode === 'percentage') return `${Number(cell.percentage || 0).toFixed(1)}%`;
    return formatPeople(cell.absoluteCount);
  };

  const columnTotals = useMemo(() => {
    const periodCount = Math.max(
      columnHeaders.length,
      ...(cohortData || []).map(row => row.periods?.length || 0),
      0
    );
    const periodHasData = Array.from({ length: periodCount }, (_, index) => {
      return (cohortData || []).some(row => {
        const cell = row.periods?.[index];
        return cell && !cell.isFuturePeriod;
      });
    });
    return {
      totalUsers: Number(serverTotals.users || 0),
      totalMetric: Number(serverTotals.total || 0),
      periodTotals: serverTotals.periodTotals || [],
      periodHasData
    };
  }, [cohortData, columnHeaders, serverTotals]);

  const getColorIntensity = (percentage) => {
    if (percentage === 100) return 'rgb(220, 53, 69)'; // Red for 100%
    const intensity = Math.max(0, Math.min(255, Math.floor((percentage / 100) * 255)));
    return `rgb(255, ${255 - intensity}, ${255 - intensity})`;
  };

  const downloadCSV = () => {
    if (!cohortData || cohortData.length === 0) return;

    const headers = ['Cohort', 'Users', 'Avg Spend', ...getColumnHeaders()];
    const rows = cohortData.map(row => {
      const values = (row.periods || []).map(cell => {
        return displayMode === 'percentage'
          ? `${Number(cell.percentage || 0).toFixed(1)}%`
          : Number(cell.absoluteCount || 0);
      });
      return [row.cohort, row.users, Number(row.avgLtv || row.total || 0), ...values];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `spend_analysis_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      className="cohort-retention-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Header
        title="Spend Analysis"
        subtitle="How many customers fall in each spend band, with average spend"
        showBackButton={true}
        backPath="/menu"
      />
      <div className="cohort-content">
        <div className="cohort-controls" onKeyDown={handleKeyPress}>
          <div className="date-inputs">
            <DatePicker
              selected={startDateObj}
              onChange={handleStartDateChange}
              selectsStart
              startDate={startDateObj}
              endDate={endDateObj}
              maxDate={endDateObj}
              dateFormat="MMM d, yyyy"
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              dropdownMode="select"
              className="date-picker-input"
              wrapperClassName="date-picker-wrapper"
              calendarClassName="custom-calendar"
              popperClassName="custom-datepicker-popper"
              placeholderText="Select start date"
            />
            <DatePicker
              selected={endDateObj}
              onChange={handleEndDateChange}
              selectsEnd
              startDate={startDateObj}
              endDate={endDateObj}
              minDate={startDateObj}
              dateFormat="MMM d, yyyy"
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              dropdownMode="select"
              className="date-picker-input"
              wrapperClassName="date-picker-wrapper"
              calendarClassName="custom-calendar"
              popperClassName="custom-datepicker-popper"
              placeholderText="Select end date"
            />
          </div>
          <select
            value={displayMode}
            onChange={(e) => handleDisplayModeChange(e.target.value)}
            className="display-mode-select"
          >
            <option value="percentage">Percentage (%)</option>
            <option value="absolute">Absolute Numbers</option>
          </select>

          {/* Location/Substore Filter */}
          <div className={`location-filter-group ${(selectedLocations.inScope.delhi.length > 0 || selectedLocations.inScope.bangalore.length > 0 || selectedLocations.inScope.blank || selectedLocations.outScope.length > 0) ? 'applied' : ''} ${showLocationDropdown ? 'dropdown-open' : ''}`}>
            <div className={`location-multiselect ${showLocationDropdown ? 'dropdown-open' : ''}`}>
              <div
                className="location-selector"
                onClick={() => !loadingLocations && setShowLocationDropdown(!showLocationDropdown)}
                style={{ cursor: loadingLocations ? 'not-allowed' : 'pointer' }}
              >
                <div className="location-display">
                  {!selectedLocations.inScope.delhi.length && !selectedLocations.inScope.bangalore.length && !selectedLocations.inScope.blank && !selectedLocations.outScope.length ? (
                    <span className="placeholder">
                      {loadingLocations ? 'Loading...' : 'Select locations...'}
                    </span>
                  ) : (
                    <span className="selected-text">
                      {[
                        ...(selectedLocations.inScope.delhi.length > 0 ? ['Delhi'] : []),
                        ...(selectedLocations.inScope.bangalore.length > 0 ? ['Bangalore'] : []),
                        ...(selectedLocations.inScope.blank ? ['Blank'] : []),
                        ...(selectedLocations.outScope.length > 0 ? ['OutScope'] : [])
                      ].join(', ')}
                    </span>
                  )}
                </div>
                <div className="location-arrow">▼</div>
              </div>

              {showLocationDropdown && !loadingLocations && (
                <div className="location-dropdown">
                  <div className="location-search">
                    <input
                      type="text"
                      placeholder="Search locations..."
                      value={locationSearchTerm}
                      onChange={(e) => setLocationSearchTerm(e.target.value)}
                      className="location-search-input"
                      onClick={(e) => e.stopPropagation()}
                      disabled={loadingLocations}
                    />
                  </div>

                  <div className="location-tree">
                    {/* InScope Section */}
                    <div className="location-category">
                      <div 
                        className="location-category-header"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          // Only toggle expansion if clicking on the expand icon or label, not the checkbox
                          if (e.target.classList.contains('location-expand-icon') || e.target.classList.contains('location-category-label')) {
                            toggleCategoryExpansion('inScope');
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedLocations.inScope.delhi.length > 0 || selectedLocations.inScope.bangalore.length > 0 || selectedLocations.inScope.blank}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (e.target.checked) {
                              handleSelectAllInScopeItems();
                            } else {
                              handleClearAllInScopeItems();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="location-checkbox"
                          disabled={loadingLocations}
                        />
                        <span className="location-category-label">InScope</span>
                        <span 
                          className="location-expand-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategoryExpansion('inScope');
                          }}
                        >
                          {expandedCategories.inScope ? '▼' : '▶'}
                        </span>
                      </div>
                      
                      {expandedCategories.inScope && (
                        <>
                          {/* Delhi */}
                          <div className="location-city">
                            <div className="location-city-header">
                              <input
                                type="checkbox"
                                checked={selectedLocations.inScope.delhi.length > 0}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (e.target.checked) {
                                    handleSelectAllInScope('Delhi');
                                  } else {
                                    handleClearAllInScope('Delhi');
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="location-checkbox"
                                disabled={loadingLocations}
                              />
                              <span className="location-city-label">Delhi</span>
                              <span 
                                className="location-expand-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleCityExpansion('delhi');
                                }}
                              >
                                {expandedCities.delhi ? '▼' : '▶'}
                              </span>
                            </div>
                            {expandedCities.delhi && (
                              <div className="location-substores" onClick={(e) => e.stopPropagation()}>
                                {(cityHubs.Delhi || [])
                                  .filter(hub => hub.toLowerCase().includes(locationSearchTerm.toLowerCase()))
                                  .map(hub => (
                                    <label 
                                      key={hub} 
                                      className="location-option"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedLocations.inScope.delhi.includes(hub)}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleLocationToggle('inScope', 'delhi', hub);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="location-checkbox"
                                        disabled={loadingLocations}
                                      />
                                      <span className="location-label">{hub}</span>
                                    </label>
                                  ))}
                              </div>
                            )}
                          </div>

                          {/* Bangalore */}
                          <div className="location-city">
                            <div className="location-city-header">
                              <input
                                type="checkbox"
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (e.target.checked) {
                                    handleSelectAllInScope('Bengaluru');
                                  } else {
                                    handleClearAllInScope('Bengaluru');
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                checked={selectedLocations.inScope.bangalore.length > 0}
                                className="location-checkbox"
                                disabled={loadingLocations}
                                id="bangalore-checkbox"
                              />
                              <label htmlFor="bangalore-checkbox" className="location-city-label">Bangalore</label>
                              <span 
                                className="location-expand-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleCityExpansion('bangalore');
                                }}
                              >
                                {expandedCities.bangalore ? '▼' : '▶'}
                              </span>
                            </div>
                            {expandedCities.bangalore && (
                              <div className="location-substores">
                                {(cityHubs.Bengaluru || [])
                                  .filter(hub => hub.toLowerCase().includes(locationSearchTerm.toLowerCase()))
                                  .map(hub => (
                                    <label 
                                      key={hub} 
                                      className="location-option"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedLocations.inScope.bangalore.includes(hub)}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleLocationToggle('inScope', 'bangalore', hub);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="location-checkbox"
                                        disabled={loadingLocations}
                                      />
                                      <span className="location-label">{hub}</span>
                                    </label>
                                  ))}
                              </div>
                            )}
                          </div>

                          {/* Blank */}
                          <label 
                            className="location-option"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedLocations.inScope.blank}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleLocationToggle('inScope', 'blank', null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="location-checkbox"
                              disabled={loadingLocations}
                            />
                            <span className="location-label">Blank</span>
                          </label>
                        </>
                      )}
                    </div>

                    {/* OutScope Section */}
                    <div className="location-category">
                      <div 
                        className="location-category-header"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          // Only toggle expansion if clicking on the expand icon or label, not the checkbox
                          if (e.target.classList.contains('location-expand-icon') || e.target.classList.contains('location-category-label')) {
                            toggleCategoryExpansion('outScope');
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedLocations.outScope.length > 0}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (e.target.checked) {
                              handleSelectAllOutScope();
                            } else {
                              handleClearAllOutScope();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="location-checkbox"
                          disabled={loadingLocations}
                        />
                        <span className="location-category-label">OutScope</span>
                        <span 
                          className="location-expand-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategoryExpansion('outScope');
                          }}
                        >
                          {expandedCategories.outScope ? '▼' : '▶'}
                        </span>
                      </div>
                      {expandedCategories.outScope && (
                        <div className="location-substores" onClick={(e) => e.stopPropagation()}>
                          {cityHubs['Pan India'] && cityHubs['Pan India'].length > 0 ? (
                            <>
                              <div className="location-substore-group-header">
                                <span className="location-group-label">Pan India ({cityHubs['Pan India'].length} hubs)</span>
                              </div>
                              {cityHubs['Pan India']
                                .filter(hub => {
                                  if (!locationSearchTerm) return true;
                                  return hub.toLowerCase().includes(locationSearchTerm.toLowerCase());
                                })
                                .map(hub => (
                                  <label 
                                    key={hub} 
                                    className="location-option"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedLocations.outScope.includes(hub)}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleLocationToggle('outScope', null, hub);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="location-checkbox"
                                      disabled={loadingLocations}
                                    />
                                    <span className="location-label">{hub}</span>
                                  </label>
                                ))}
                            </>
                          ) : (
                            <div className="location-substore-group-header">
                              <span className="location-group-label">No OutScope hubs available</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <button 
            onClick={handleSearch}
            className="search-button"
            disabled={loading || !startDate || !endDate}
          >
            {loading ? 'Loading...' : 'Show Spend Data'}
          </button>
          
          <button 
            onClick={downloadCSV}
            className="download-button"
            disabled={!cohortData || cohortData.length === 0}
            title={`Download CSV (${displayMode === 'percentage' ? 'Percentages' : 'Absolute Numbers'})`}
          >
            <span className="download-emoji">⬇️</span>
          </button>
        </div>

        {error && <Alert severity="error" message={error} />}

        {cacheInfo && (
          <div className="cache-info">
            <div className={`cache-status ${cacheInfo.fromCache ? 'cache-hit' : 'cache-miss'}`}>
              {cacheInfo.fromCache ? (
                cacheInfo.cacheType === 'exact' ? '⚡ Cached Data' : 
                cacheInfo.cacheType === 'filtered' ? '🔍 Filtered Cache' :
                cacheInfo.cacheType === 'prefix_tail' ? '⚡ Cache + new days' : '⚡ Cached Data'
              ) : '🔄 Live Data'}
              {cacheInfo.substoreFiltered && (
                <span style={{ marginLeft: '10px', fontSize: '0.85rem', color: '#52c41a' }}>
                  📍 Substore Filtered
                </span>
              )}
            </div>
            <div className="cache-details">
              {cacheInfo.fromCache ? (
                <span>
                  Generated: {new Date(cacheInfo.generatedAt).toLocaleString()}
                  {cacheInfo.cacheType === 'filtered' && cacheInfo.originalCacheRange && (
                    <span className="cache-filter-info">
                      {' '}(Filtered from broader cache: {new Date(cacheInfo.originalCacheRange.startDate).toLocaleDateString()} - {new Date(cacheInfo.originalCacheRange.endDate).toLocaleDateString()})
                    </span>
                  )}
                  {cacheInfo.substoreFiltered && cacheInfo.substores && (
                    <span className="cache-filter-info" style={{ marginLeft: '10px', color: '#52c41a' }}>
                      Substores: {cacheInfo.substores.length} selected
                    </span>
                  )}
                </span>
              ) : (
                <span>Processed in: {cacheInfo.processingTimeMs}ms</span>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="cohort-table-container">
            <div className="table-header-info">
              <span className="display-mode-indicator">
                <div className="skeleton-text" style={{ width: '200px', height: '20px' }}></div>
              </span>
            </div>
            <table className="cohort-table skeleton-table">
              <thead>
                <tr>
                  <th><div className="skeleton-text" style={{ width: '80px', height: '16px' }}></div></th>
                  <th><div className="skeleton-text" style={{ width: '60px', height: '16px' }}></div></th>
                  {getColumnHeaders().map((header, index) => (
                    <th key={index}>
                      <div className="skeleton-text" style={{ width: '70px', height: '16px' }}></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    <td><div className="skeleton-text" style={{ width: '90px', height: '20px' }}></div></td>
                    <td><div className="skeleton-text" style={{ width: '50px', height: '20px' }}></div></td>
                    {getColumnHeaders().map((_, cellIndex) => (
                      <td key={cellIndex}>
                        <div className="skeleton-text" style={{ width: '40px', height: '20px' }}></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : cohortData && cohortData.length > 0 ? (
          <div className="cohort-table-container">
            <div className="table-header-info">
              <span className="display-mode-indicator">
                Showing: <strong>People in each spend band · {displayMode === 'percentage' ? '% of cohort users' : 'Customer counts'}</strong>
              </span>
              <span className="users-legend">
                <span className="users-legend-item users-legend-total">Users</span>
                <span className="users-legend-item users-legend-repeat">Avg Spend</span>
              </span>
              <span className="current-month-note" style={{
                marginLeft: '20px',
                fontSize: '0.85rem',
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
              </span>
            </div>
            <table className="cohort-table">
              <thead>
                <tr>
                  <th>Cohort</th>
                  <th className="users-col">
                    Users
                    <div className="users-metric">
                      <span className="users-metric-total">{formatHeaderTotal(columnTotals.totalUsers)}</span>
                    </div>
                  </th>
                  <th className="users-col">
                    Avg Spend
                    <div className="users-metric">
                      <span className="users-metric-repeat">{formatMoney(columnTotals.totalMetric)}</span>
                    </div>
                  </th>
                  {getColumnHeaders().map((header, index) => (
                    <th key={index}>
                      {header}
                      {displayMode !== 'percentage' && columnTotals.periodHasData[index] && (
                        <span className="column-total"> ({formatHeaderTotal(columnTotals.periodTotals[index] || 0)})</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohortData.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td>{row.cohort}</td>
                    <td className="users-col">
                      <div className="users-metric">
                        <span className="users-metric-total">{Number(row.users || 0).toLocaleString('en-IN')}</span>
                      </div>
                    </td>
                    <td className="users-col">
                      <div className="users-metric">
                        <span className="users-metric-repeat">{formatMoney(row.avgLtv || row.total)}</span>
                      </div>
                    </td>
                    {(row.periods || []).map((cell, cellIndex) => {
                      const displayValue = formatCellValue(cell);

                      const isCurrent = cell.isCurrentPeriod === true;
                      const isFuture = cell.isFuturePeriod === true;

                      // Future periods: show dash, greyed out
                      if (isFuture) {
                        return (
                          <td
                            key={cellIndex}
                            style={{
                              backgroundColor: '#f0f0f0',
                              color: '#999',
                              textAlign: 'center'
                            }}
                            title="Future period"
                          >
                            -
                          </td>
                        );
                      }

                      // Current running period: show data with indicator
                      if (isCurrent) {
                        return (
                          <td
                            key={cellIndex}
                            style={{
                              backgroundColor: displayMode === 'percentage' 
                                ? getColorIntensity(cell.percentage) 
                                : '#f8f9fa',
                              color: displayMode === 'percentage' && cell.percentage > 70 ? 'white' : 'black',
                              border: '2px solid #fbbf24',
                              position: 'relative'
                            }}
                            title="Current period - data is being updated daily"
                          >
                            {displayValue}
                            <span style={{
                              position: 'absolute',
                              top: '2px',
                              right: '2px',
                              fontSize: '0.7em',
                              color: '#f59e0b',
                              fontWeight: 'bold'
                            }}>●</span>
                          </td>
                        );
                      }

                      return (
                        <td
                          key={cellIndex}
                          style={{
                            backgroundColor: displayMode === 'percentage' 
                              ? getColorIntensity(cell.percentage) 
                              : '#f8f9fa',
                            color: displayMode === 'percentage' && cell.percentage > 70 ? 'white' : 'black'
                          }}
                        >
                          {displayValue}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : hasSearched ? (
          <div className="no-data-message">
            <div className="no-data-icon">📊</div>
            <div>No spend analysis data for the selected date range</div>
            <div className="no-data-subtitle">Try adjusting your date range</div>
          </div>
        ) : (
          <div className="welcome-message">
            <div className="welcome-icon">₹</div>
            <div className="welcome-title">Ready to Analyze Spend</div>
            <div className="welcome-subtitle">
              For each acquisition cohort, see average spend and how many customers fall in each spend band.
            </div>
            <div className="welcome-tips">
              <div className="tip">💰 <strong>Spend:</strong> till-date sum of non-admin order <code>sub_total</code></div>
              <div className="tip">👥 <strong>Bands:</strong> Under 250, 250-500, 500-1000, 1000-2000, 2000-5000, 5000-10000, Above 10000</div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default LtvAnalysis;
